import { useEffect, useState, type ChangeEvent } from 'react';
import { getReviews } from '../../services/reviewRequests';
import { ReviewData } from '../../types/Review';
import ReviewsContainer from './ReviewsContainer/ReviewsContainer';
import ReviewsModal from './ReviewsModal/ReviewsModal';

const LIMIT = 20;
const INITIAL_OFFSET = 0;
const INITIAL_REVIEW_LENGTH = 5;

type ReviewsProps = {
  productId: number;
  brandName: string;
  productName: string;
  env: 'dev' | 'prod';
  isLoggedIn: boolean;
  scrollToRef?: HTMLElement;
};

const defaultSort = 'RECENTLY_ADDED';

const Reviews = ({
  productId,
  brandName,
  productName,
  env,
  isLoggedIn,
  scrollToRef,
}: ReviewsProps) => {
  const [selectedSort, setSelectedSort] = useState(defaultSort);
  const [reviews, setReviews] = useState<ReviewData[]>([]);
  const [totalReviews, setTotalReviews] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [offset, setOffset] = useState(INITIAL_REVIEW_LENGTH);

  const hasMoreReviews = totalReviews > INITIAL_REVIEW_LENGTH && reviews.length < totalReviews;

  const onHideReview = (reviewId: number) => {
    setReviews((reviews) => reviews.filter((review) => review.reviewId !== reviewId));
  };

  const fetchMoreReviews = async () => {
    try {
      const response = await getReviews(productId, selectedSort, LIMIT, offset, env, isLoggedIn);
      const { items: reviews } = response?.data || {};
      setReviews((prevState) => {
        if (prevState.length) {
          return [...prevState, ...reviews];
        }
        return reviews;
      });
      setOffset((prevState) => (prevState += LIMIT));
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await getReviews(
          productId,
          selectedSort,
          INITIAL_REVIEW_LENGTH,
          INITIAL_OFFSET,
          env,
          isLoggedIn,
        );
        const { items: reviews, totalItems } = response?.data || {};
        setReviews(reviews);
        setTotalReviews(totalItems);
      } catch (err) {
        console.error(err);
      }
    };
    fetchReviews();
    setOffset(INITIAL_REVIEW_LENGTH);
  }, [selectedSort, productId]);

  const handleDropdown = (e: ChangeEvent<HTMLSelectElement>) => {
    setSelectedSort(e.target.value);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  return (
    <>
      <ReviewsContainer
        env={env}
        reviews={reviews}
        totalReviews={totalReviews}
        selectedSort={selectedSort}
        handleOpenModal={handleOpenModal}
        handleDropdown={handleDropdown}
        fetchMoreReviews={fetchMoreReviews}
        hasMoreReviews={hasMoreReviews}
        isLoggedIn={isLoggedIn}
        scrollToRef={scrollToRef}
        onHideReview={onHideReview}
      />
      <ReviewsModal
        isOpen={modalOpen}
        brandName={brandName}
        productName={productName}
        env={env}
        reviews={reviews}
        totalReviews={totalReviews}
        selectedSort={selectedSort}
        handleCloseModal={handleCloseModal}
        productId={productId}
        isLoggedIn={isLoggedIn}
      />
    </>
  );
};

export default Reviews;
